import { TranslatableError } from '@/i18n/errors';
import {
  similarityTransform,
  type AlignTransform,
  type Matrix,
  type Point,
} from '@/lib/align';

/**
 * Client-side encoding of a captured frame.
 *
 * Photos never pass through the backend: the browser normalises and re-encodes
 * here, then PUTs the result straight to S3. Re-encoding through a canvas is
 * also what discards EXIF — including GPS — before the bytes ever leave the
 * device. The worker strips metadata again server-side, because this path is an
 * optimisation rather than something the API can take on trust.
 */

/** Every photo is written at exactly this size, aligned or not. */
export const OUTPUT_WIDTH = 1080;
export const OUTPUT_HEIGHT = 1350;

export type EncodedMime = 'image/avif' | 'image/webp';

export interface EyePair {
  left: Point;
  right: Point;
}

export interface EncodedPhoto {
  blob: Blob;
  mime: EncodedMime;
  width: number;
  height: number;
  /** Lowercase hex SHA-256 the worker re-checks against the uploaded object. */
  checksum: string;
  /** The correction baked into the pixels, kept so frames can be re-rendered. */
  transform: AlignTransform | null;
}

const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

let avifSupport: Promise<boolean> | null = null;

/**
 * Whether this browser can *encode* AVIF, which is narrower than decoding it.
 * Chrome ships no canvas AVIF encoder at all and Safari encodes only WebP, so
 * the answer has to come from asking the canvas rather than the user agent.
 */
export function supportsAvifEncoding(): Promise<boolean> {
  avifSupport ??= (async () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/avif');
    });

    // A browser that cannot honour the type silently falls back to PNG.
    return blob?.type === 'image/avif';
  })();

  return avifSupport;
}

interface CoverFit {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Fills the output frame, cropping the overflow.
 *
 * Every photo gets the same dimensions even when the camera hands back a
 * different aspect ratio — a timelapse whose frame size changes between days
 * jumps, which is precisely what this whole step exists to avoid.
 */
function coverFit(sourceWidth: number, sourceHeight: number): CoverFit {
  const scale = Math.max(OUTPUT_WIDTH / sourceWidth, OUTPUT_HEIGHT / sourceHeight);
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;

  return {
    x: (OUTPUT_WIDTH - width) / 2,
    y: (OUTPUT_HEIGHT - height) / 2,
    width,
    height,
  };
}

/**
 * Re-expresses an eye position, normalised against the source frame, as a
 * fraction of the output frame once the cover fit has been applied.
 */
function inOutputFrame(eye: Point, fit: CoverFit): Point {
  return {
    x: (fit.x + eye.x * fit.width) / OUTPUT_WIDTH,
    y: (fit.y + eye.y * fit.height) / OUTPUT_HEIGHT,
  };
}

async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function encodeBitmap(
  bitmap: ImageBitmap,
  eyes: EyePair | null,
): Promise<EncodedPhoto> {
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;

  const context = canvas.getContext('2d');

  if (!context) {
    throw new TranslatableError('errors.canvas_context');
  }

  const fit = coverFit(bitmap.width, bitmap.height);

  const transform = eyes
    ? similarityTransform(
        inOutputFrame(eyes.left, fit),
        inOutputFrame(eyes.right, fit),
        OUTPUT_WIDTH,
        OUTPUT_HEIGHT,
      )
    : null;

  const matrix = transform?.matrix ?? IDENTITY;

  // Rotation and scaling leave the corners empty; without this they would be
  // transparent, which encodes to black in AVIF.
  context.fillStyle = '#000000';
  context.fillRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);

  context.setTransform(...matrix);
  context.drawImage(bitmap, fit.x, fit.y, fit.width, fit.height);
  context.setTransform(...IDENTITY);
  bitmap.close();

  const mime: EncodedMime = (await supportsAvifEncoding()) ? 'image/avif' : 'image/webp';

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, mime, 0.82);
  });

  if (!blob) {
    throw new TranslatableError('errors.encode_failed');
  }

  return {
    blob,
    mime,
    width: OUTPUT_WIDTH,
    height: OUTPUT_HEIGHT,
    checksum: await sha256Hex(blob),
    transform,
  };
}

/**
 * Grabs the current video frame.
 *
 * The preview is mirrored in CSS because an unmirrored self-view feels wrong,
 * but the stored frame deliberately is not: flipping it would put every capture
 * at odds with a gallery import of the same face.
 */
export async function encodeFromVideo(
  video: HTMLVideoElement,
  eyes: EyePair | null = null,
): Promise<EncodedPhoto> {
  if (!video.videoWidth || !video.videoHeight) {
    throw new TranslatableError('errors.no_frame');
  }

  return encodeBitmap(await createImageBitmap(video), eyes);
}

/** Gallery import: the same pipeline, so imports and captures stay alike. */
export async function encodeFromFile(
  file: File,
  eyes: EyePair | null = null,
): Promise<EncodedPhoto> {
  return encodeBitmap(await createImageBitmap(file), eyes);
}
